import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";

export default function SettingsAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) return <div className="h-96 rounded-3xl bg-white animate-pulse" />;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        ...form,
        delivery_fee: Number(form.delivery_fee) || 0,
        ...Object.fromEntries(["svc_domicile_price", "svc_visio_price", "svc_prenatal_price", "svc_reprise_price"].map((k) => [k, Number(form[k]) || 0])),
      });
      toast.success("Réglages enregistrés — le site est mis à jour !");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, props = {}, testid) => (
    <label className="block text-sm font-medium text-ink">
      {label}
      <input
        data-testid={testid}
        value={form[key] ?? ""}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition"
        {...props}
      />
    </label>
  );

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Réglages de la boutique</h1>
      <p className="text-sm text-ink/60 mt-1">Ces informations s'affichent sur tout le site — modifiez-les à tout moment.</p>

      <form data-testid="admin-settings-form" onSubmit={save} className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-3xl border border-line p-6 md:p-8 space-y-4">
          <h2 className="font-serif text-xl text-teal-dark mb-2">Identité</h2>
          {field("Nom de la boutique", "shop_name", { required: true }, "admin-settings-shop-name")}
          {field("Slogan", "tagline", {}, "admin-settings-tagline")}
          {field("Adresse affichée", "address", {}, "admin-settings-address")}
          <div className="rounded-2xl bg-cream-light/60 border border-line p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-ink">
              <input type="checkbox" data-testid="admin-settings-banner-active" checked={!!form.banner_active} onChange={(e) => setForm({ ...form, banner_active: e.target.checked })} className="h-4 w-4 accent-teal" />
              Afficher le bandeau promo en haut du site
            </label>
            {field("Texte du bandeau", "announcement", {}, "admin-settings-announcement")}
            {field("Code promo affiché (optionnel — doit exister et être actif dans Codes promo)", "banner_code", { placeholder: "MAMAN10" }, "admin-settings-banner-code")}
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-line p-6 md:p-8 space-y-4">
          <h2 className="font-serif text-xl text-teal-dark mb-2">Contact & livraison</h2>
          {field("Téléphone", "phone", {}, "admin-settings-phone")}
          {field("WhatsApp", "whatsapp", {}, "admin-settings-whatsapp")}
          {field("Lien de votre page d'avis Google (optionnel)", "google_reviews_url", { placeholder: "https://g.page/r/..." }, "admin-settings-google-reviews")}
          {field("Lien Calendly pour réserver un accompagnement (optionnel — vide = réservation par WhatsApp)", "booking_url", { placeholder: "https://calendly.com/votre-nom" }, "admin-settings-booking-url")}
          {field("Frais de livraison (FCFA) — 0 = communiqués à la cliente hors du site", "delivery_fee", { type: "number", min: 0 }, "admin-settings-delivery-fee")}
          <div className="flex items-start gap-3 rounded-2xl bg-mint/15 p-4">
            <Mail size={18} className="text-teal shrink-0 mt-0.5" />
            <div className="flex-1">
              <label className="text-sm font-medium text-ink">
                Email de notification
                <input
                  data-testid="admin-settings-notify-email"
                  type="email"
                  value={form.notify_email ?? ""}
                  onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
                  placeholder="votre@email.com"
                  className="mt-1.5 w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-teal transition"
                />
              </label>
              <p className="mt-2 text-xs text-ink/60">Vous recevrez un email à chaque nouvelle commande. Laissez vide pour désactiver.</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-line p-6 md:p-8 space-y-4">
          <h2 className="font-serif text-xl text-teal-dark mb-2">Page d'accueil</h2>
          {field("Sur-titre du hero", "hero_overline", {}, "admin-settings-hero-overline")}
          {field("Titre du hero — ligne 1", "hero_title_1", {}, "admin-settings-hero-title-1")}
          {field("Titre du hero — ligne 2", "hero_title_2", {}, "admin-settings-hero-title-2")}
          <label className="block text-sm font-medium text-ink">
            Texte d'introduction
            <textarea rows={3} data-testid="admin-settings-hero-sub" value={form.hero_sub ?? ""} onChange={(e) => setForm({ ...form, hero_sub: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal transition" />
          </label>
        </div>

        <div className="bg-white rounded-3xl border border-line p-6 md:p-8 space-y-4">
          <h2 className="font-serif text-xl text-teal-dark mb-2">Référencement Google (SEO)</h2>
          {field("Titre SEO (titre de l'onglet)", "seo_title", {}, "admin-settings-seo-title")}
          <label className="block text-sm font-medium text-ink">
            Description SEO (affichée dans Google)
            <textarea rows={3} data-testid="admin-settings-seo-description" value={form.seo_description ?? ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal transition" />
          </label>
        </div>

        <div className="bg-white rounded-3xl border border-line p-6 md:p-8 space-y-4 lg:col-span-2">
          <h2 className="font-serif text-xl text-teal-dark mb-2">Tarifs accompagnement (page Accompagnement)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["domicile", "Consultation à domicile"],
              ["visio", "Consultation en visio"],
              ["prenatal", "Atelier préparation prénatale"],
              ["reprise", "Atelier reprise du travail"],
            ].map(([k, label]) => (
              <div key={k} className="rounded-2xl bg-cream-light/60 border border-line p-4 grid grid-cols-2 gap-3">
                <p className="col-span-2 text-sm font-medium text-teal-dark">{label}</p>
                {field("Prix (FCFA)", `svc_${k}_price`, { type: "number", min: 0 }, `admin-settings-svc-${k}-price`)}
                {field("Durée / format", `svc_${k}_duration`, { placeholder: "1h en visio" }, `admin-settings-svc-${k}-duration`)}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <button type="submit" data-testid="admin-settings-save" disabled={saving} className="rounded-full bg-teal px-10 py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 size={16} className="animate-spin" />}
            {saving ? "Enregistrement…" : "Enregistrer les réglages"}
          </button>
        </div>
      </form>
    </div>
  );
}
